import { network } from 'boot/network';
import { onSnapshot } from 'firebase/firestore';

import GenericDB from './generic-db';

export default class BillingItemsDB extends GenericDB {
  constructor() {
    super('billingItems');
  }

  /**
   * A clinic's items, live, ordered by service date.
   *
   * @param {string} clinicId
   * @param {(documents: Object[]) => void} onDocuments
   * @param {(error: Error) => void} onError
   * @returns {() => void} stops listening
   */
  watchClinic(clinicId, onDocuments, onError) {
    return this.#watch([['clinicId', '==', clinicId]], 'serviceDate', onDocuments, onError);
  }

  /**
   * One client's items held for one reason, live: the projector rewrites them as the records they
   * are held on are fixed, so what they still lack changes while the reason stays the same.
   *
   * @param {{clinicId: string, clientId: string, holdReason: string}} held
   * @param {(documents: Object[]) => void} onDocuments
   * @param {(error: Error) => void} onError
   * @returns {() => void} stops listening
   */
  watchHeld({ clinicId, clientId, holdReason }, onDocuments, onError) {
    return this.#watch([
      ['clinicId', '==', clinicId],
      ['clientId', '==', clientId],
      ['holdReasons', 'array-contains', holdReason],
    ], null, onDocuments, onError);
  }

  /**
   * The items held for any of several reasons that name one subject in `field`, live.
   *
   * @param {{clinicId: string, field: string, value: string, holdReasons: string[]}} held
   * @param {(documents: Object[]) => void} onDocuments
   * @param {(error: Error) => void} onError
   * @returns {() => void} stops listening
   */
  watchHeldAbout({
    clinicId, field, value, holdReasons,
  }, onDocuments, onError) {
    return this.#watch([
      ['clinicId', '==', clinicId],
      [field, '==', value],
      ['holdReasons', 'array-contains-any', holdReasons],
    ], null, onDocuments, onError);
  }

  /** Offline there is no listener to attach, so the cached read is delivered once. */
  #watch(constraints, orderBy, onDocuments, onError) {
    if (network.offlineMode) {
      let active = true;

      this.readAll(constraints, orderBy).then(
        (documents) => active && onDocuments(documents),
        (error) => active && onError(error),
      );

      return () => {
        active = false;
      };
    }

    return onSnapshot(
      this.getQuery(constraints, orderBy),
      (snapshot) => onDocuments(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))),
      onError,
    );
  }

  /**
   * DERIVED AND SERVER-WRITTEN. The projector computes every document from a session activity and
   * the configuration around it, and reconciles what it computed against what is stored. The rules
   * give a browser no write path at all, so there is nothing to write here and never will be: a
   * wrong item is fixed by fixing the configuration it came from, which is the only correction that
   * survives the next projection run.
   *
   * CLINIC-SCOPED, and the scope is `clinicId` rather than the bare `clinic` its neighbours use.
   * The field names here mirror the claim vocabulary the item is built in, so every read must
   * constrain on `clinicId` — the rules require it too, and an unscoped read reaches nothing rather
   * than reaching too far.
   */
}
